import classNames from "classnames";
import {useMemo, useState} from "react";

import {Button} from "@/components/button/Button.tsx";
import {Dot} from "@/components/dot/Dot.tsx";
import {SearchInput} from "@/components/searchInput/SearchInput.tsx";
import {EllipsisText} from "@/components/typography/EllipsisText.tsx";
import {NetworkProbeResult} from "@/lib/types/network.ts";

import {originLabel} from "./networkProbe.utils.ts";

import styles from "./NetworkProbeList.module.scss";

export const NetworkProbeList = ({
  probes,
  selectedIndex,
  onSelect,
}: {
  probes: NetworkProbeResult[];
  selectedIndex: number;
  onSelect: (index: number) => void;
}) => {
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return probes
      .map((probe, index) => ({probe, index}))
      .filter(({probe}) => {
        if (!q) return true;
        const {city, country, network} = probe.origin;
        return [city, country, network].some((f) => f.toLowerCase().includes(q));
      });
  }, [probes, query]);

  return (
    <div>
      <div className={styles.searchBar}>
        <SearchInput onChange={setQuery} />
      </div>
      <ul className={styles.list}>
        {filtered.length === 0 && <li className={styles.emptyHint}>No locations match “{query}”.</li>}
        {filtered.map(({probe, index}) => (
          <li key={index}>
            <Button
              variant="probeListRow"
              size="bsInput"
              className={classNames({
                [styles.listRowSelected]: index === selectedIndex,
              })}
              onClick={() => onSelect(index)}
            >
              <Dot aria-hidden="true" className={styles.listDot} variant={probe.status === "PASS" ? "green" : "red"} />
              <span className="visually-hidden">{probe.status === "PASS" ? "Passed" : "Failed"}</span>
              <span className={styles.rowBody}>
                <span className={styles.rowName}>{originLabel(probe.origin)}</span>
                <span className={styles.rowSubLine}>
                  <EllipsisText className={styles.subNetwork}>
                    {probe.origin.network} AS{probe.origin.asn}
                  </EllipsisText>
                  {probe.traceroute && (
                    <>
                      <span className={styles.subDivider} />
                      <span className={styles.subHops}>{probe.traceroute.hops.length} hops</span>
                    </>
                  )}
                </span>
              </span>
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
};
